import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import Icons from 'constants/icons';

import { modalsActions } from 'features/modals/modalsSlice';

import EditIcon from 'assets/images/icons/edit.svg';
import BinIcon from 'assets/images/icons/bin.svg';
import ImportantIcon from 'assets/images/icons/events/important.svg';

import EventText from 'components/EventText';
import LoadingSpinner from 'atoms/LoadingSpinner';
import ErrorIcon from 'atoms/ErrorIcon';

import StyledEvent from './StyledEvent';
import { EventProps } from './EventTypes';
import { useSelector } from 'react-redux';
import { editEvent } from 'features/input/thunks';
import { getFormattedEventTime } from 'features/match/helpers/calculateTime';
import getEventText from '../EventText/helpers/getEventText';
import { MatchEventType, matchStatusEvents, uneditableAndNonDeletableEvents } from '../../enums/matchEventType';
import { keyboardActions } from '../../features/keyboard/keyboardSlice';
import { ActiveFocusItemName } from '../../features/keyboard/keyboardSliceTypes';
import { MatchEventStatus } from '../../enums/matchEventStatus';
import { selectActiveMatchEvents } from '../../features/match/matchSlice';
import Period from '../../enums/period';
import getPeriodLengthFromMatchTime from '../MatchWatch/helpers/getPeriodLengthFromMatchTime';
import { selectMatch } from '../../features/match/matchSlice';
import getExtraTimePeriodLengthFromMatchLength from '../MatchWatch/helpers/getExtraTimePeriodLengthFromMatchLength';
import { useAppDispatch } from '../../hooks/hooks';

const Event: React.FC<EventProps> = ({
  matchEvent,
  assistMatchEvent,
  substituteMatchEvent,
  offenceMatchEvent,
  fouledMatchEvent,
  penaltyMissMatchEvent,
  penaltyGoalMatchEvent,
  penaltySaveMatchEvent,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const match = useSelector(selectMatch);

  const activeMatchEvents = useSelector(selectActiveMatchEvents);
  const { matchEventTypeId, comment } = matchEvent;
  const matchEventName = MatchEventType[matchEventTypeId];
  const icon = Icons[matchEventTypeId];
  const importantAndNoIcon = matchEvent.important && !icon;
  const isActive = activeMatchEvents.some((activeMatchEvent) => activeMatchEvent.id === matchEvent.id);

  let isLoading = matchEvent.internal ? matchEvent.internal.loading : false;
  if (assistMatchEvent && assistMatchEvent.internal && assistMatchEvent.internal.loading) {
    isLoading = true;
  }
  if (fouledMatchEvent && fouledMatchEvent.internal && fouledMatchEvent.internal.loading) {
    isLoading = true;
  }
  if (offenceMatchEvent && offenceMatchEvent.internal && offenceMatchEvent.internal.loading) {
    isLoading = true;
  }
  if (penaltySaveMatchEvent && penaltySaveMatchEvent.internal && penaltySaveMatchEvent.internal.loading) {
    isLoading = true;
  }
  if (substituteMatchEvent && substituteMatchEvent.internal && substituteMatchEvent.internal.loading) {
    isLoading = true;
  }

  const hasErrorOccurred = matchEvent.internal ? !matchEvent.internal.loading && matchEvent.internal.error : false;
  const isMatchStatusEvent = matchStatusEvents.includes(matchEvent.matchEventTypeId);
  let isUneditableAndNonDeletable = uneditableAndNonDeletableEvents.includes(matchEvent.matchEventTypeId);
  if (
    matchEvent.internal &&
    (matchEvent.internal.status === MatchEventStatus.DELETING ||
      matchEvent.internal.status === MatchEventStatus.DELETE_ERROR)
  ) {
    isUneditableAndNonDeletable = true;
  }
  const showImportantIcon = importantAndNoIcon && !isMatchStatusEvent;

  const initiateDelete = () => {
    dispatch(
      modalsActions.openDeleteEventModal({
        event: matchEvent,
        assistEvent: assistMatchEvent,
        substituteEvent: substituteMatchEvent,
        fouledEvent: fouledMatchEvent,
        offenceEvent: offenceMatchEvent,
        penaltyMissEvent: penaltyMissMatchEvent,
        penaltyGoalEvent: penaltyGoalMatchEvent,
        penaltySaveMatchEvent: penaltySaveMatchEvent,
      }),
    );
  };

  const onEdit = () => {
    dispatch(editEvent(matchEvent));
    dispatch(keyboardActions.setActiveFocusItem(ActiveFocusItemName.COMMENT));
  };

  const eventMessage = getEventText(matchEvent);

  let time = matchEvent.time ?? undefined;
  let timeClassName = 'green';
  if (
    (time &&
      (matchEvent.overtime ||
        (matchEvent.periodId === Period.FIRST_HALF &&
          time === getPeriodLengthFromMatchTime(match.matchLength) &&
          (matchEvent.seconds ?? 0) > 0) ||
        (matchEvent.periodId === Period.SECOND_HALF &&
          time === getPeriodLengthFromMatchTime(match.matchLength) * 2 &&
          (matchEvent.seconds ?? 0) > 0))) ||
    (matchEvent.periodId === Period.FIRST_HALF_EXTRA_TIME &&
      time ===
        getPeriodLengthFromMatchTime(match.matchLength) * 2 +
          getExtraTimePeriodLengthFromMatchLength(match.matchLength) &&
      (matchEvent.seconds ?? 0) > 0) ||
    (matchEvent.periodId === Period.SECOND_HALF_EXTRA_TIME &&
      time ===
        getPeriodLengthFromMatchTime(match.matchLength) * 2 +
          getExtraTimePeriodLengthFromMatchLength(match.matchLength) * 2 &&
      (matchEvent.seconds ?? 0) > 0)
  ) {
    time += matchEvent.overtime ?? 0;
    timeClassName = 'red';
  }

  return (
    <StyledEvent
      className={classNames({
        goal: matchEventTypeId === MatchEventType.GOAL,
        yellowCard:
          matchEventTypeId === MatchEventType.YELLOW_CARD || matchEventTypeId === MatchEventType.SECOND_YELLOW_CARD,
        redCard: matchEventTypeId === MatchEventType.RED_CARD,
        active: isActive,
      })}
    >
      <div className={'index ' + timeClassName}>{getFormattedEventTime(time, matchEvent.seconds)}</div>
      <div className="line" />
      {icon && <img className="icon" src={icon} alt={matchEventName} />}
      {showImportantIcon && (
        <img className="icon" src={ImportantIcon} alt={intl.formatMessage({ id: 'toggles.important' })} />
      )}
      <div className="event-content">
        <div className="event-text-container">
          <EventText message={eventMessage} />
        </div>
        <div className={'comment' + (eventMessage ? ' commentWithHeader' : '')}>{comment && comment}</div>
      </div>
      {!isUneditableAndNonDeletable && (
        <div className="event-actions">
          {!isMatchStatusEvent && <img src={EditIcon} alt="Edit" onClick={onEdit} />}
          {!isLoading && <img className="delete" src={BinIcon} alt="Delete" onClick={initiateDelete} />}
        </div>
      )}
      <div className="request-status-container">
        <LoadingSpinner loading={!!isLoading} />
        <div className="error-container">
          <ErrorIcon error={!!hasErrorOccurred} />
        </div>
      </div>
    </StyledEvent>
  );
};

export default Event;
